import { useCallback } from 'react';
import * as pas from 'js/hooks/auth/pas';
import { useDispatch } from 'react-redux';
import { prop } from 'ramda';
import { storageService } from '@core/js/services/storage.service';
import { useUserSelector } from '@core/js/store/hooks';
import { LAYER, useOnClose } from '@theme/js/components/common/PopupManager';
import { showCommonPopupAction } from '@core/js/components/common/popups/CommonPopup';
import { useClosingSync } from '@core/js/components/system/App/hooks/usePushNotifier/hooks';
import { handleBonusNotificationInfo } from '@core/js/components/system/App/hooks/usePushNotifier/common/handleBonusNotificationInfo';
import { showFreeSpin2BonusPopupAction } from '@core/js/components/popups/FreeSpin2BonusPopup';

import { WAITING_MESSAGE_DISABLE_ALL } from '@core/js/hooks/auth/hooks/constants';
import { useGetWelcomeWaitingMessageTypes } from '@core/js/hooks/auth/hooks/useGetWelcomeWaitingMessageTypes';

export const useGetWaitingMessages = () => {
    const dispatch = useDispatch();
    const syncClose = useClosingSync();
    const onClose = useOnClose();
    const lang = useUserSelector(prop('lang'));
    const getWelcomeWaitingMessageTypes = useGetWelcomeWaitingMessageTypes();

    return useCallback(async (data) => {
        const messageTypes = getWelcomeWaitingMessageTypes(data);

        if (messageTypes === WAITING_MESSAGE_DISABLE_ALL || messageTypes === '') {
            return;
        }

        const response = await pas.getWaitingMessages({ messageTypes });
        const {
            PlayerActionShowMessage = [],
            PlayerActionShowLoginMessage = [],
            PlayerActionShowDialog = [],
            PlayerActionShowDialog2 = []
        } = response?.data?.actions || {};

        const regexp = /<body[^>]*>(.*)<\/body>/gis;

        if (storageService.local.getItem('isAfterRegistration')) {
            storageService.local.removeItem('isAfterRegistration');
            return;
        }

        PlayerActionShowMessage
            .filter(playerMessage => Boolean(playerMessage.message))
            .forEach(playerMessage => {
                const search = regexp.exec(playerMessage.message);
                const content = search ? search[1] : playerMessage.message;

                dispatch(showCommonPopupAction({
                    layer: LAYER.LOGIN_MESSAGES,
                    content,
                    className: 'waiting-message-push-popup',
                    title: 'playtech.mobile.system.message.congratulations'
                }));
            });

        PlayerActionShowLoginMessage
            .filter(playerMessage => Boolean(playerMessage.message))
            .forEach(playerMessage => {
                const search = regexp.exec(playerMessage.message);
                const content = search ? search[1] : playerMessage.message;

                dispatch(showCommonPopupAction({
                    title: 'playtech.mobile.system.welcomeTitle',
                    layer: LAYER.LOGIN_MESSAGES,
                    className: 'welcome-popup',
                    content,
                    buttons: [
                        {
                            onClick: onClose,
                            text: 'playtech.mobile.system.welcomeNextButton'
                        }
                    ]
                }));
            });

        PlayerActionShowDialog
            .filter(playerMessage => Boolean(playerMessage.message))
            .forEach(playerMessage => {
                const { dialogId, dialogType, message, parameters: { templateId, showGamesList } = {} } = playerMessage;
                const search = regexp.exec(message);
                const content = search ? search[1] : message;

                switch (dialogType) {
                    case 'bonusConfirmation': {
                        const actionInfo = {
                            dialogId,
                            templateId,
                            showGamesList,
                            message: content,
                            layer: LAYER.LOGIN_MESSAGES
                        };

                        handleBonusNotificationInfo({ actionInfo, isConfirmation: true }, { lang, syncClose, dispatch })
                            .then(action => dispatch(action))
                            .catch(error => console.error('could not dispatch action', error));

                        break;
                    }

                    default:
                        break;
                }
            });

        PlayerActionShowDialog2
            .forEach((action) => {
                const { message, dialogId, showGamesList, betConfigurations, bonusDescription, gameCodeNames } = action;

                dispatch(showFreeSpin2BonusPopupAction({
                    id: dialogId,
                    title: bonusDescription,
                    layer: LAYER.LOGIN_MESSAGES,
                    message,
                    betConfigurations,
                    gameCodeNames,
                    showGamesList,
                    dialogId
                }));
            });
    }, [dispatch, lang, getWelcomeWaitingMessageTypes, syncClose, onClose]);
};
